import { useCallback } from "react";
import { AgentContext } from "../types/agentConfig";
import { GenericAgentConversationService } from "../services/GenericAgentConversationService";

/**
 * Generic hook for agent chat functionality
 */
export function useGenericAgentChat(context: AgentContext) {
  const service = new GenericAgentConversationService(context.agentType);

  /**
   * Create a new conversation
   */
  const createConversation = useCallback(
    async (title: string): Promise<string> => {
      return await service.createConversation(context, title);
    },
    [service, context]
  );

  /**
   * Add a message to a conversation
   */
  const addMessage = useCallback(
    async (conversationId: string, text: string): Promise<void> => {
      await service.addMessage(context, conversationId, text);
    },
    [service, context]
  );

  /**
   * Add a system initiation message to a conversation
   */
  const addSystemInitiationMessage = useCallback(
    async (conversationId: string, text: string): Promise<void> => {
      await service.addSystemInitiationMessage(context, conversationId, text);
    },
    [service, context]
  );

  /**
   * Get conversations path for the current context
   */
  const getConversationsPath = useCallback((): string => {
    return service.getConversationsPath(context);
  }, [service, context]);

  /**
   * Get messages path for a specific conversation
   */
  const getMessagesPath = useCallback(
    (conversationId: string): string => {
      return service.getMessagesPath(context, conversationId);
    },
    [service, context]
  );

  /**
   * Get agent configuration
   */
  const getAgentConfig = useCallback(() => {
    return service.getConfig();
  }, [service]);

  return {
    createConversation,
    addMessage,
    addSystemInitiationMessage,
    getConversationsPath,
    getMessagesPath,
    getAgentConfig,
  };
}